import { ScreenHeader } from '@/components/layout/screen-header';
import type {
  LeaderboardCategory,
  LeaderboardEntry,
} from '@/types/leaderboard';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useMemo } from 'react';
import { ActivityIndicator, FlatList, Text, View } from 'react-native';
import { CategoryTabs } from './components/category-tabs';
import { LeaderboardRow } from './components/leaderboard-row';

type ListItem =
  | { type: 'entry'; entry: LeaderboardEntry }
  | { type: 'gap' };

interface LeaderboardScreenProps {
  entries: LeaderboardEntry[];
  currentUser: LeaderboardEntry | null;
  category: LeaderboardCategory;
  onCategoryChange: (category: LeaderboardCategory) => void;
  isLoading: boolean;
}

export function LeaderboardScreen({
  entries,
  currentUser,
  category,
  onCategoryChange,
  isLoading,
}: LeaderboardScreenProps) {
  const listData = useMemo((): ListItem[] => {
    const items: ListItem[] = entries.map((entry) => ({ type: 'entry', entry }));

    if (!currentUser) return items;

    const alreadyInList = entries.some((e) => e.userId === currentUser.userId);
    if (!alreadyInList) {
      items.push({ type: 'gap' });
      items.push({ type: 'entry', entry: currentUser });
    }

    return items;
  }, [entries, currentUser]);

  return (
    <>
      <ScreenHeader title="Weekly Leaderboards" showBackButton />
      <CategoryTabs selected={category} onSelect={onCategoryChange} />

      {isLoading ? (
        <View className="flex-1 items-center justify-center py-16">
          <ActivityIndicator size="large" />
        </View>
      ) : entries.length === 0 ? (
        <View className="flex-1 items-center justify-center px-8 py-16">
          <Ionicons
            name="trophy-outline"
            size={48}
            className="text-muted-foreground"
          />
          <Text className="text-xl font-semibold text-center mt-4 mb-2 text-foreground">
            No activity this week
          </Text>
          <Text className="text-center text-muted-foreground">
            Be the first to check in this week and claim the top spot!
          </Text>
        </View>
      ) : (
        <FlatList
          data={listData}
          keyExtractor={(item, index) =>
            item.type === 'gap' ? `gap-${index}` : item.entry.userId
          }
          renderItem={({ item }) => {
            if (item.type === 'gap') {
              return (
                <View className="items-center py-2">
                  <Text className="text-muted-foreground text-lg tracking-widest">
                    ...
                  </Text>
                </View>
              );
            }
            return (
              <LeaderboardRow
                entry={item.entry}
                category={category}
                isCurrentUser={currentUser?.userId === item.entry.userId}
              />
            );
          }}
          contentContainerStyle={{ paddingBottom: 32 }}
        />
      )}
    </>
  );
}
